import { useState, useRef, useEffect } from "react";

const SCROLL_ZONE_SIZE = 80;
const SCROLL_SPEED = 8;

const useReorderable = (items, onReorder, { scrollRef, getDragImage } = {}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const scrollAnimationRef = useRef(null);

  const stopAutoScroll = () => {
    if (scrollAnimationRef.current === null) return;
    cancelAnimationFrame(scrollAnimationRef.current);
    scrollAnimationRef.current = null;
  };

  const startAutoScroll = (direction) => {
    stopAutoScroll();
    const scrollDelta = direction === "down" ? SCROLL_SPEED : -SCROLL_SPEED;

    const scroll = () => {
      const container = scrollRef?.current;
      if (!container) return;
      container.scrollTop += scrollDelta;
      scrollAnimationRef.current = requestAnimationFrame(scroll);
    };

    scrollAnimationRef.current = requestAnimationFrame(scroll);
  };

  useEffect(() => {
    return () => {
      if (scrollAnimationRef.current) cancelAnimationFrame(scrollAnimationRef.current);
    };
  }, []);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    const dragImage = getDragImage?.(index);
    if (dragImage) e.dataTransfer.setDragImage(dragImage, 12, 12);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (scrollRef?.current) {
      const containerRect = scrollRef.current.getBoundingClientRect();
      const mouseY = e.clientY;

      if (mouseY < containerRect.top + SCROLL_ZONE_SIZE) {
        startAutoScroll("up");
      } else if (mouseY > containerRect.bottom - SCROLL_ZONE_SIZE) {
        startAutoScroll("down");
      } else {
        stopAutoScroll();
      }
    }

    if (draggedIndex === null || draggedIndex === index) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;
    const isTopHalf = relativeY < rect.height / 2;
    const newDropIndex = isTopHalf ? index : index + 1;

    const isNoMove = newDropIndex === draggedIndex || newDropIndex === draggedIndex + 1;
    if (isNoMove) {
      if (dropIndex !== null) setDropIndex(null);
      return;
    }

    if (dropIndex !== newDropIndex) setDropIndex(newDropIndex);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    stopAutoScroll();

    if (draggedIndex === null || dropIndex === null) {
      setDraggedIndex(null);
      setDropIndex(null);
      return;
    }

    const newItems = [...items];
    const [dragged] = newItems.splice(draggedIndex, 1);
    const insertAt = dropIndex > draggedIndex ? dropIndex - 1 : dropIndex;
    newItems.splice(insertAt, 0, dragged);

    onReorder(newItems, { fromIndex: draggedIndex, toIndex: insertAt });
    setDraggedIndex(null);
    setDropIndex(null);
  };

  const handleDragEnd = () => {
    stopAutoScroll();
    setDraggedIndex(null);
    setDropIndex(null);
  };

  const getDragProps = (index) => ({
    draggable: true,
    onDragStart: (e) => handleDragStart(e, index),
    onDragOver: (e) => handleDragOver(e, index),
    onDrop: handleDrop,
    onDragEnd: handleDragEnd,
  });

  const isDragging = (index) => draggedIndex === index;

  const showDropBefore = (index) => dropIndex === index;

  const showDropAfter = (index, totalItems) => dropIndex === index + 1 && index === totalItems - 1;

  return { getDragProps, isDragging, showDropBefore, showDropAfter, draggedIndex };
};

export default useReorderable;
